import { createApi } from "@reduxjs/toolkit/query/react";
import type {
  CandidateJobsResponse,
  ActiveJob,
  CandidateJobsRequest,
  InterviewStructureApiResponse,
  CreateJobRoleApiResponse,
  CreateJobRoleApiRequest,
  UpdateJobStatusApiRequest,
  UpdateJobStatusApiResponse,
  CreateJobPollingApiResponse,
  CreateJobPollingApiRequest,
} from "./types";
import { HTTP_METHODS, JOBS, JOBS_ROUTES } from "@cointervue/shared/constants";
import { createBaseQueryWithReauth } from "@cointervue/shared/api";
import { logout } from "../../store/authSlice";
import { buildJobRoleFormData } from "@cointervue/shared";
import { invalidateCandidateStatusOnSuccess } from "./candidatesApi";

export const getJobsApi = createApi({
  reducerPath: "getJobsApi",
  baseQuery: createBaseQueryWithReauth(JOBS.href, logout),
  tagTypes: ["Jobs"],

  endpoints: (builder) => ({
    getCandidateJobs: builder.query<
      CandidateJobsResponse,
      CandidateJobsRequest
    >({
      query: ({ status = "", search = "", sortBy = "", token = "" }) => ({
        url: "",
        method: HTTP_METHODS.GET,
        credentials: "include",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        params: {
          ...(status && { status }),
          ...(search && { search }),
          ...(sortBy && { sortBy }),
          ...(token && { token }),
        },
      }),
      providesTags: (result) =>
        result?.data?.jobs
          ? [
              { type: "Jobs", id: "LIST" },
              ...result.data.jobs.map((job) => ({
                type: "Jobs" as const,
                id: job.jobId,
              })),
            ]
          : [{ type: "Jobs", id: "LIST" }],
    }),

    getJobById: builder.query<
      {
        job: ActiveJob;
      },
      string
    >({
      query: (jobId) => ({
        url: `/${jobId}`,
        method: HTTP_METHODS.GET,
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      providesTags: (_res, _err, jobId) => [{ type: "Jobs", id: jobId }],
    }),
    interviewStructure: builder.query<InterviewStructureApiResponse, string>({
      query: (jobId) => ({
        url: `${JOBS_ROUTES.interviewStructure}/${jobId}`,
        method: HTTP_METHODS.GET,
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      providesTags: (_res, _err, jobId) => [{ type: "Jobs", id: jobId }],
    }),
    editJobRole: builder.mutation<
      CreateJobRoleApiResponse,
      { jobId: string; data: Partial<CreateJobRoleApiRequest> }
    >({
      query: ({ data, jobId }) => ({
        url: JOBS_ROUTES.editJobRole(jobId),
        method: HTTP_METHODS.PUT,
        body: buildJobRoleFormData(data),
      }),
      invalidatesTags: (_res, _err, { jobId }) => [
        { type: "Jobs", id: "LIST" },
        { type: "Jobs", id: jobId },
      ],
      onQueryStarted: invalidateCandidateStatusOnSuccess,
    }),
    createJobRole: builder.mutation<
      CreateJobRoleApiResponse,
      CreateJobRoleApiRequest & { cloneJobId?: string }
    >({
      query: ({ cloneJobId, isDirtyForm, ...data }) => ({
        url: JOBS_ROUTES.createJobRole,
        method: HTTP_METHODS.POST,
        body: buildJobRoleFormData(data),
        params: { cloneJobId, isDirtyForm },
      }),
      invalidatesTags: (result) => [
        { type: "Jobs" as const, id: "LIST" },
        ...(typeof result?.job === "string"
          ? [{ type: "Jobs" as const, id: result.job }]
          : result?.job?.jobId
            ? [{ type: "Jobs" as const, id: result.job.jobId }]
            : []),
      ],
      onQueryStarted: invalidateCandidateStatusOnSuccess,
    }),
    updateJobStatus: builder.mutation<
      UpdateJobStatusApiResponse,
      UpdateJobStatusApiRequest
    >({
      query: ({ jobId, status }) => ({
        url: `/${jobId}`,
        method: HTTP_METHODS.PATCH,
        body: { status },
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "Jobs" as const, id: "LIST" },
        { type: "Jobs" as const, id: jobId },
      ],
      onQueryStarted: invalidateCandidateStatusOnSuccess,
    }),
    createJobRolePolling: builder.query<
      CreateJobPollingApiResponse,
      CreateJobPollingApiRequest
    >({
      query: ({ jobId }) => ({
        url: JOBS_ROUTES.createJobRolePolling,
        method: HTTP_METHODS.GET,
        params: { jobId },
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetCandidateJobsQuery,
  useGetJobByIdQuery,
  useLazyGetJobByIdQuery,
  useInterviewStructureQuery,
  useEditJobRoleMutation,
  useCreateJobRoleMutation,
  useUpdateJobStatusMutation,
  useCreateJobRolePollingQuery,
  useLazyCreateJobRolePollingQuery,
} = getJobsApi;
